import { createContext, Dispatch, FC, ReactNode, SetStateAction, useContext, useState } from "react"
import { PlatformAdmin } from "../types/Platform"
import { Permission, Tenant, User } from "../types/User"

export interface AuthState {
    user?: User
    tenants?: Tenant[]
    permissions: string[]
    /** Present when the signed-in person is also (or only) a platform administrator. */
    platformAdmin?: PlatformAdmin
}

interface AuthContextState {
    auth: AuthState
    setAuth: Dispatch<SetStateAction<AuthState>>
    selectedTenant: Tenant | null
    setSelectedTenant: Dispatch<SetStateAction<Tenant | null>>
    isAllTenantsSelected: boolean
    setIsAllTenantsSelected: Dispatch<SetStateAction<boolean>>
    userHasPermission: (permission: Permission | Permission[]) => boolean
    userIsAdmin: () => boolean
    userIsPlatformAdmin: () => boolean
    isAuthenticated: () => boolean
    getClientId: () => number
}

const AuthContext = createContext<AuthContextState | undefined>(undefined)
AuthContext.displayName = "AuthContext"

const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [auth, setAuth] = useState<AuthState>({ permissions: [] })
    const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null)
    const [isAllTenantsSelected, setIsAllTenantsSelected] = useState<boolean>(false)

    const hasPermission = (permission: Permission): boolean => {
        // Platform administration is its own identity: a client admin never gets it.
        if (permission === Permission.PlatformAdmin) return !!auth.platformAdmin
        if (auth.user?.isAdmin) return true
        return auth.permissions.includes(Permission[permission])
    }

    const userHasPermission = (permissionsToCheck: Permission | Permission[]): boolean => {
        const permissionList = Array.isArray(permissionsToCheck) ? permissionsToCheck : [permissionsToCheck]
        return permissionList.some(hasPermission)
      }

    const userIsAdmin = (): boolean => {
        return auth.user?.isAdmin || false
    }

    const userIsPlatformAdmin = (): boolean => !!auth.platformAdmin

    const isAuthenticated = (): boolean => !!auth.user || !!auth.platformAdmin

    const getClientId = (): number => {
        return auth.user?.client?.id || 0
    }

    return (
        <AuthContext.Provider value={{ auth, setAuth, selectedTenant, setSelectedTenant , isAllTenantsSelected, setIsAllTenantsSelected, userHasPermission, userIsAdmin, userIsPlatformAdmin, isAuthenticated, getClientId }}>
            {children}
        </AuthContext.Provider>
    )
}

const useAuth = (): AuthContextState => {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider")
    }

    return context
}

export { AuthProvider, useAuth }
